import { roundedRectArea } from '@/utils/power.util';
import { computed } from 'vue';
import { useIsIntranet } from './use-is-intranet';
import { ElectronApi } from '@/services/electron.api';
import { GlobalConfig } from '@/services/gloabl.config';

export type WatchfaceDeviceShape = 'circle' | 'square' | 'run';
export type WatchfaceDeviceType =
  | 'L61Simulator'
  | 'L61'
  | 'L67'
  | 'N60'
  | 'N65'
  | 'M67'
  | 'M62'
  | 'M65S'
  | 'M66'
  | 'N62'
  | 'N62S'
  | 'N65A'
  | 'N65B'
  | 'N65C'
  | 'N66'
  | 'N67'
  | 'O61'
  | 'O62'
  | 'O62M'
  | 'O63'
  | 'Q61'
  | 'Q63'
  | 'Q66'
  | 'O65'
  | 'O65M'
  | 'O66'
  | 'P62'
  | 'P65'
  | 'P67';
export const watchfaceShapeOptions: {
  value: WatchfaceDeviceShape;
  label: string;
}[] = [
  { value: 'circle', label: '圆形' },
  { value: 'square', label: '方形' },
  { value: 'run', label: '跑道' }
];

export interface WatchfaceDeviceInfo {
  deviceType: WatchfaceDeviceType;
  shape: WatchfaceDeviceShape;
  size: string;
  preview: { width: number; height: number };
  previewEnhanced?: { width: number; height: number };
  editScale?: number;
  radius?: number;

  deviceTypeAlias?: string[];
  /**
   * 内存限制最大值：单位kb
   */
  maxRamLimit?: number;
  /**
   * bin文件最大值：单位kb
   */
  maxBinLimit?: number;
  // 状态
  status: 'published' | 'unpublished';
  //产品名称
  productName?: string;

  maxShapeCount?: number;

  // 序列帧数目限制，仅对内部版本有效
  defaultPointerFps?: 1 | 30 | 60;

  /** watchOS 版本 默认为vela */
  watchOS?: 'vela' | 'android';

  // 序列帧资源规则
  spriteRules?: {
    count?: number;
    size?: { w: number; h: number };
  };
}

/**
 * 所有设备
 */

const wholeWatchfaceDevices: WatchfaceDeviceInfo[] = [
  {
    deviceType: 'N62',
    shape: 'circle',
    size: '464x464',
    preview: { width: 326, height: 326 },
    editScale: 0.5517,
    maxRamLimit: 2560,
    maxBinLimit: 4 * 1024,
    status: 'published',
    productName: 'Xiaomi watch S3',
    defaultPointerFps: 30
  },
  {
    deviceType: 'N62S',
    shape: 'circle',
    size: '464x464',
    preview: { width: 326, height: 326 },
    editScale: 0.5517,
    maxBinLimit: 4 * 1024,
    maxRamLimit: 2560,
    status: 'published',
    productName: 'Xiaomi Watch S4 Sport',
    defaultPointerFps: 30
  },
  {
    deviceType: 'O62',
    shape: 'circle',
    size: '464x464',
    previewEnhanced: { width: 464, height: 464 },
    preview: { width: 326, height: 326 },
    editScale: 0.5517,
    status: 'published',
    maxRamLimit: 2560,
    spriteRules: {
      count: ElectronApi.isProfessional() ? 200 : 60
    },
    productName: 'Xiaomi Watch S4',
    defaultPointerFps: 30
  },
  {
    deviceType: 'O62M',
    shape: 'circle',
    size: '464x464',
    previewEnhanced: { width: 464, height: 464 },
    preview: { width: 326, height: 326 },
    editScale: 0.5517,
    status: 'published',
    maxRamLimit: 5120,
    spriteRules: {
      count: ElectronApi.isProfessional() ? 200 : 60
    },
    productName: 'Xiaomi Watch S4 eSIM',
    defaultPointerFps: 30
  },
  {
    deviceType: 'O63',
    shape: 'circle',
    size: '464x464',
    previewEnhanced: { width: 464, height: 464 },
    preview: { width: 464, height: 464 },
    editScale: 0.5517,
    status: 'published',
    productName: 'Xiaomi Watch S4 41mm',
    maxRamLimit: 2560,
    spriteRules: {
      count: ElectronApi.isProfessional() ? 200 : 60
    },
    defaultPointerFps: 30
  },
  {
    deviceType: 'Q61',
    shape: 'circle',
    size: '480x480',
    preview: { width: 480, height: 480 },
    maxRamLimit: 2560,
    maxBinLimit: 4 * 1024,
    editScale: 0.5517,
    status: 'unpublished',
    defaultPointerFps: 30,
    productName: '',
    spriteRules: {
      count: 200
    },
    watchOS: 'android'
  },
  {
    deviceType: 'Q63',
    shape: 'circle',
    size: '464x464',
    previewEnhanced: { width: 464, height: 464 },
    preview: { width: 464, height: 464 },
    editScale: 0.5517,
    status: 'unpublished',
    maxRamLimit: 2560,
    spriteRules: {
      count: 200
    },
    defaultPointerFps: 30
  },
  {
    deviceType: 'Q66',
    shape: 'run',
    size: '212x520',
    preview: { width: 212, height: 520 },
    editScale: 134 / 328,
    radius: 104,
    maxRamLimit: 1024,
    maxBinLimit: 1 * 1024,
    status: 'unpublished',
    productName: '',
    defaultPointerFps: 30,
    spriteRules: {
      count: 60
    }
  },
  {
    deviceType: 'M62',
    shape: 'circle',
    size: '466x466',
    preview: { width: 246, height: 246 },
    status: 'unpublished'
  },
  {
    deviceType: 'L61',
    deviceTypeAlias: ['L61Simulator'],
    shape: 'circle',
    size: '480x480',
    preview: { width: 280, height: 280 },
    maxRamLimit: 2560,
    maxBinLimit: 4 * 1024,
    status: 'unpublished'
    // productName: 'Xiaomi watch S1 pro',
  },
  {
    deviceType: 'O61',
    shape: 'circle',
    size: '480x480',
    preview: { width: 368, height: 368 },
    maxRamLimit: 2560,
    maxBinLimit: 4 * 1024,
    editScale: 0.5517,
    status: 'published',
    defaultPointerFps: 30,
    productName: 'Xiaomi Watch 5',
    spriteRules: {
      count: ElectronApi.isProfessional() ? 200 : 60
    },
    watchOS: 'android'
  },
  {
    deviceType: 'N60',
    shape: 'circle',
    size: '466x466',
    preview: { width: 246, height: 246 },
    editScale: 0.7,
    maxRamLimit: 2560,
    maxBinLimit: 4 * 1024,
    // feat: 外部版本去掉N60
    status: 'unpublished',
    productName: '小米腕部心电血压记录仪'
  },
  {
    deviceType: 'M65S',
    shape: 'square',
    size: '390x450',
    preview: { width: 234, height: 270 },
    radius: 86,
    status: 'unpublished'
  },
  {
    deviceType: 'N65',
    shape: 'square',
    size: '390x450',
    preview: { width: 220, height: 250 },
    radius: 90,
    editScale: 0.6,
    maxRamLimit: 2560,
    maxBinLimit: 2 * 1024,
    status: 'published',
    productName: 'Redmi Watch 4'
  },
  {
    deviceType: 'N65A',
    shape: 'square',
    size: '320x385',
    preview: { width: 180, height: 216 },
    radius: 82,
    status: 'unpublished'
  },
  {
    deviceType: 'N65B',
    shape: 'square',
    size: '410x502',
    preview: { width: 244, height: 298 },
    radius: 116,
    status: 'unpublished'
  },
  {
    deviceType: 'N65C',
    shape: 'square',
    size: '390x450',
    preview: { width: 224, height: 258 },
    radius: 88,
    // maxRamLimit: 796,
    status: 'unpublished'
  },
  {
    deviceType: 'M67',
    shape: 'square',
    size: '336x480',
    preview: { width: 230, height: 328 },
    editScale: 0.6833,
    radius: 48,
    maxRamLimit: 2560,
    maxBinLimit: 1.5 * 1024,
    status: 'published',
    productName: '小米手环8 pro',
    spriteRules: {
      count: 30,
      size: {
        w: 200,
        h: 200
      }
    }
  },
  {
    deviceType: 'N67',
    shape: 'square',
    size: '336x480',
    preview: { width: 230, height: 328 },
    editScale: 0.6833,
    radius: 48,
    maxRamLimit: 2560,
    maxBinLimit: 1.5 * 1024,
    status: 'published',
    productName: '小米手环9 Pro'
  },
  {
    deviceType: 'L67',
    shape: 'square',
    size: '280x456',
    preview: { width: 220, height: 358 },
    status: 'unpublished'
    // productName: '小米手环7 pro',
  },
  {
    deviceType: 'O65',
    shape: 'square',
    size: '432x514',
    preview: { width: 432, height: 514 },
    radius: 103,
    status: 'published',
    editScale: 0.6,
    maxRamLimit: 2560,
    productName: 'Redmi Watch 5'
  },
  {
    deviceType: 'O65M',
    shape: 'square',
    size: '432x514',
    preview: { width: 432, height: 514 },
    radius: 103,
    maxRamLimit: 2560,
    status: 'published',
    editScale: 0.6,
    productName: 'Redmi Watch 5 esim',
    spriteRules: {
      count: ElectronApi.isProfessional() ? 200 : 60
    },
    defaultPointerFps: 30
  },
  {
    deviceType: 'M66',
    shape: 'run',
    size: '192x490',
    preview: { width: 122, height: 310 },
    editScale: 0.6326,
    radius: 280,
    maxRamLimit: 600,
    maxBinLimit: 2.5 * 1024,
    status: 'published',
    productName: '手环8',
    maxShapeCount: 30
  },
  {
    deviceType: 'N66',
    shape: 'run',
    size: '192x490',
    preview: { width: 122, height: 310 },
    editScale: 122 / 192,
    radius: 280,
    maxRamLimit: 1024,
    maxBinLimit: 1 * 1024,
    status: 'published',
    productName: '小米手环9'
  },
  {
    deviceType: 'O66',
    shape: 'run',
    size: '212x520',
    preview: { width: 134, height: 328 },
    editScale: 134 / 212,
    radius: 104,
    maxRamLimit: 1024,
    maxBinLimit: 1 * 1024,
    status: 'published',
    productName: '小米手环10',
    defaultPointerFps: 30
  },
  {
    deviceType: 'P62',
    shape: 'circle',
    size: '480x480',
    preview: { width: 480, height: 480 },
    maxRamLimit: 2560,
    maxBinLimit: 4 * 1024,
    editScale: 0.55,
    status: 'published',
    productName: 'Xiaomi Watch S5',
    spriteRules: {
      count: ElectronApi.isProfessional() ? 200 : 60
    },
    defaultPointerFps: 30
  },
  {
    deviceType: 'P65',
    shape: 'square',
    size: '432x514',
    preview: { width: 432, height: 514 },
    radius: 108,
    editScale: 0.6,
    status: 'published',
    productName: 'Redmi Watch 6',
    maxRamLimit: 2560
  },
  {
    deviceType: 'P67',
    shape: 'square',
    size: '336x480',
    preview: { width: 336, height: 480 },
    spriteRules: {
      count: 60
    },
    radius: 48,
    editScale: 0.6833,
    status: 'published',
    productName: '小米手环10 Pro',
    maxRamLimit: 2560,
    defaultPointerFps: 30
  }
];

export const getWatchfaceDeviceRadius = (deviceType: string) => {
  const watchface = wholeWatchfaceDevices.find(
    (item) => item.deviceType == deviceType
  );
  if (!watchface) return 0;
  const { size, shape, radius } = watchface;
  if (radius != undefined) return radius;

  // 如果未配置，使用默认值
  const [w = 0, h = 0] = size.split('x');
  const width = Number(w);
  const height = Number(h);
  let _radius = 0;
  switch (shape) {
    case 'run':
      _radius = Math.min(width, height) / 2;
      break;
    case 'square':
      _radius = 64;
      break;
    case 'circle':
    default:
      _radius = width / 2;
  }

  return _radius;
};

/**
 * 获取表盘面积
 * @returns
 */
export const getWatchfaceArea = (deviceType: string) => {
  const { size = '' } =
    wholeWatchfaceDevices.find((item) => item.deviceType == deviceType) ?? {};
  const [w = '0', h = '0'] = size?.split('x') ?? [];
  const width = Number(w);
  const height = Number(h);
  return roundedRectArea(width, height, getWatchfaceDeviceRadius(deviceType));
};

export const useWatchfaceDevices = () => {
  const { isIntranet } = useIsIntranet();
  const wholeDevices = computed(() => {
    const isIntranetAndPro =
      (ElectronApi.isProfessional() || ElectronApi.isStaging()) &&
      isIntranet.value;
    return [...wholeWatchfaceDevices].filter(
      (item) =>
        item.status == 'published' ||
        // 未发布的设置只允许pro版本且可访问内网时使用
        (item.status == 'unpublished' && isIntranetAndPro)
    );
  });
  const canUse = (deviceType: string) => {
    return wholeDevices.value.some((item) => item.deviceType == deviceType);
  };
  const findDeivce = (deviceType: string) => {
    return wholeDevices.value.find((item) => item.deviceType == deviceType);
  };

  const getProductNameIfNeed = (deviceType: string) => {
    if (!GlobalConfig.showDeviceTypeProductionName) return deviceType;
    const item = findDeivce(deviceType);
    return item?.productName ?? deviceType;
  };
  return {
    wholeDevices,
    canUse,
    findDeivce,
    getProductNameIfNeed
  };
};
